import React, { useEffect, useState } from "react";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_DropdownArrow } from "@danishagro/shared/src/components/atoms/DropdownArrow/DropdownArrow.component";
import {
    useDynamicOverlay,
    DA_DynamicOverlayWrapper,
} from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";
import { DA_SelectOption } from "@danishagro/shared/src/components/atoms/Select/Select.props";
import { DA_InputWrapper } from "@danishagro/shared/src/components/atoms/InputWrapper/InputWrapper.component";
import { DA_Icon, DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import S from "./MobileDeliveryPointSelect.module.scss";

export interface MobileDeliveryPointSelectProps {
    options: DA_SelectOption[];
    value: string;
    onChange: (option: DA_SelectOption) => void;
    placeholder?: string;
    isDisabled?: boolean;
    selectedDeliveryAddress?: string;
    selectedDeliveryAddressLabel?: string;
    bottomSheetStyle?: boolean;
}

export const MobileDeliveryPointSelect: React.FC<MobileDeliveryPointSelectProps> = ({
    options,
    value,
    onChange,
    placeholder,
    isDisabled,
    selectedDeliveryAddress: _selectedDeliveryAddress,
    selectedDeliveryAddressLabel,
    bottomSheetStyle,
}) => {
    const [selectedOption, setSelectedOption] = useState<DA_SelectOption | null>(
        options.find((option) => option.value === value) || null
    );

    const { getDictionaryString } = useTranslations();
    const { showOverlay, closeOverlay } = useDynamicOverlay();

    useEffect(() => {
        setSelectedOption(options.find((option) => option.value === value) || null);
    }, [value, options]);

    // Create a component that manages its own local state
    const OverlayContent = () => {
        const [localTempSelection, setLocalTempSelection] = useState<DA_SelectOption | null>(
            selectedOption
        );

        const handleLocalOptionClick = (option: DA_SelectOption) => {
            setLocalTempSelection(option);
        };

        const handleLocalSave = () => {
            if (localTempSelection) {
                onChange(localTempSelection);
                setSelectedOption(localTempSelection);
            }
            closeOverlay();
        };

        const handleLocalCancel = () => {
            closeOverlay();
        };

        return (
            <DA_DynamicOverlayWrapper
                content={
                    <div>
                        <div className={S.introText}>
                            {selectedDeliveryAddressLabel
                                ? `${getDictionaryString(
                                      "siloAndHouses.mobileDeliveryAddressesPointsHelperText",
                                      {
                                          address: selectedDeliveryAddressLabel,
                                      }
                                  )}`
                                : getDictionaryString(
                                      "siloAndHouses.mobileDeliveryAddressesPointsHelperTextDefault",
                                      {
                                          uppercaseFirst: true,
                                      }
                                  )}
                            <span className={S.smallText}>
                                {getDictionaryString("siloAndHouses.deliveryPointTooltipText")}
                            </span>
                        </div>
                        {options.map((option) => (
                            <button
                                onClick={() => handleLocalOptionClick(option)}
                                key={String(option.value)}
                                className={cn(
                                    S.deliveryOptionButton,
                                    localTempSelection &&
                                        localTempSelection.value === option.value &&
                                        S.selected
                                )}
                            >
                                <div>
                                    <div>{option.label}</div>
                                    {option.extra && (
                                        <div className={S.extraText}>{option.extra}</div>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                }
                footer={
                    <div className={S.mobileDeliveryFooter}>
                        <DA_Button
                            onClick={handleLocalCancel}
                            isGhost={true}
                            title={getDictionaryString("close")}
                        />
                        <DA_Button
                            onClick={handleLocalSave}
                            title={getDictionaryString("FilterSaveButton")}
                        />
                    </div>
                }
            />
        );
    };

    const handleOpenOverlay = () => {
        if (isDisabled) return;

        showOverlay({
            id: `mobile-delivery-point-select-${Date.now()}`,
            title: `${getDictionaryString("siloAndHouses.mobileDeliveryAddressesPointsTitle")}`,
            content: <OverlayContent />,
        });
    };

    const displayText = selectedOption
        ? selectedOption.label
        : placeholder || getDictionaryString("siloAndHouses.mobileDeliveryAddressesPointsTitle");

    return (
        <DA_InputWrapper
            label={getDictionaryString("siloAndHouses.deliveryPoint")}
            forId="deliver-addressesPoints-mobile"
            className={bottomSheetStyle && S.bottomSheetStyle}
            overlayHidden
        >
            <button
                className={cn(S.mobileDeliveryButton, isDisabled && S.disabled)}
                onClick={handleOpenOverlay}
                disabled={isDisabled}
                id="deliver-addressesPoints-mobile"
            >
                <div className={S.mobileDeliveryButtonContent}>
                    <span className={cn(S.mobileDeliveryLabel, !selectedOption && S.placeholder)}>
                        {displayText}
                    </span>
                </div>
                {bottomSheetStyle ? (
                    <DA_Icon
                        name={DA_IconNames.ChevronRight}
                        className={S.bottomSheetStyleRightIcon}
                    />
                ) : (
                    <div className={S.mobileDeliveryArrow}>
                        <DA_DropdownArrow small />
                    </div>
                )}
            </button>
        </DA_InputWrapper>
    );
};
